// @ts-check

/**
 * @typedef {{
 *   isIdle: () => boolean,
 *   idleCheckIntervalMs?: number,
 * }} ServiceWorkerReloadOptions
 */

/**
 * @param {ServiceWorkerReloadOptions} options
 * @returns {Promise<void>}
 */
export async function setupServiceWorkerReloadWhenIdle(options) {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  let isServiceWorkerRefreshing = false;
  /** @type {Promise<void> | null} */
  let safeActivationCheckPromise = null;
  const idleCheckIntervalMs = options.idleCheckIntervalMs || 1000;

  try {
    await navigator.serviceWorker.ready;
  } catch (error) {
    console.error("[app] service worker not ready", error);
    return;
  }

  async function runSafeActivationCheck() {
    if (safeActivationCheckPromise) {
      await safeActivationCheckPromise;
      return;
    }

    safeActivationCheckPromise = runSafeActivationCheckOnce();
    try {
      await safeActivationCheckPromise;
    } finally {
      safeActivationCheckPromise = null;
    }
  }

  async function runSafeActivationCheckOnce() {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return;
    }

    await tryActivateWaitingWorker(registration, options, idleCheckIntervalMs);
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  const updateRegistration = registration;

  async function runBackgroundUpdateCheck() {
    await requestServiceWorkerUpdate(updateRegistration);
    await runSafeActivationCheck();
  }

  /**
   * @param {ServiceWorker} installingWorker
   * @returns {void}
   */
  function watchInstallingWorker(installingWorker) {
    installingWorker.addEventListener("statechange", function () {
      if (installingWorker.state !== "installed") {
        return;
      }

      void runSafeActivationCheck();
    });
  }

  function watchRegistrationInstallingWorker() {
    const installingWorker = updateRegistration.installing;
    if (!installingWorker) {
      return;
    }

    watchInstallingWorker(installingWorker);
  }

  updateRegistration.addEventListener(
    "updatefound",
    watchRegistrationInstallingWorker,
  );
  watchRegistrationInstallingWorker();

  await runSafeActivationCheck();
  void runBackgroundUpdateCheck();

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });

  window.addEventListener("pageshow", function () {
    if (document.visibilityState !== "visible") {
      return;
    }

    void runSafeActivationCheck();
    void runBackgroundUpdateCheck();
  });

  navigator.serviceWorker.addEventListener("controllerchange", function () {
    if (isServiceWorkerRefreshing) {
      return;
    }

    isServiceWorkerRefreshing = true;
    window.location.reload();
  });
}

/**
 * @returns {boolean}
 */
function isHomeRoute() {
  const pathname = window.location.pathname;
  const result = pathname === "/" || pathname === "/index.html";
  return result;
}

/**
 * @returns {boolean}
 */
function canActivateWaitingWorker() {
  if (!isHomeRoute()) {
    return false;
  }

  if (document.visibilityState !== "visible") {
    return false;
  }

  return true;
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
async function getServiceWorkerRegistration() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return null;
    }

    return registration;
  } catch (error) {
    console.error("[app] failed to get service worker registration", error);
  }

  return null;
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @param {ServiceWorkerReloadOptions} options
 * @param {number} idleCheckIntervalMs
 * @returns {Promise<void>}
 */
async function tryActivateWaitingWorker(
  registration,
  options,
  idleCheckIntervalMs,
) {
  if (!registration.waiting) {
    return;
  }

  if (!canActivateWaitingWorker()) {
    return;
  }

  await waitForAppIdle(options, idleCheckIntervalMs);

  if (!canActivateWaitingWorker()) {
    return;
  }

  const waitingWorker = registration.waiting;
  if (!waitingWorker) {
    return;
  }

  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

/**
 * @param {ServiceWorkerReloadOptions} options
 * @param {number} idleCheckIntervalMs
 * @returns {Promise<void>}
 */
async function waitForAppIdle(options, idleCheckIntervalMs) {
  while (!options.isIdle()) {
    await delay(idleCheckIntervalMs);
  }
}

/**
 * @param {number} durationMs
 * @returns {Promise<void>}
 */
function delay(durationMs) {
  return new Promise(function (resolve) {
    window.setTimeout(resolve, durationMs);
  });
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<void>}
 */
async function requestServiceWorkerUpdate(registration) {
  try {
    await registration.update();
  } catch (error) {
    console.debug("[app] service worker update check failed", error);
  }
}
